<template>
  <SfModalPersistent
    :cancel-label="cancelLabel"
    :cancel-tooltip="cancelTooltip"
    :scrollable-content="true"
    modal-id="input-automation-steps-modal"
    @close="handleClose"
  >
    <template #title>{{ titleLabel }}</template>
    <template #body>
      <component :is="bodyComponent" v-if="bodyComponent" />
    </template>
    <template #footer-right>
      <component
        :is="footerComponent"
        v-if="footerComponent"
        :should-close-on-review="shouldCloseOnReview"
        :should-decline-proposal-on-edit="shouldDeclineProposalOnEdit"
        @analyze="handleSave"
      />
    </template>
  </SfModalPersistent>
</template>

<script setup lang="ts">
import {
  ErrorMessage,
  LastOCRRequestProposalStatus,
  LastOCRRequestStatus,
  UIState,
} from '../types'
import { isProposedDocument } from '../predicates'
import { isExistingDocument } from '@app/shared/predicates'

import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount } from 'vue'

import EditUI from './input-automation-steps-edit.vue'
import EditFooter from './input-automation-steps-edit-footer.vue'
import ErrorUI from './input-automation-steps-error.vue'
import ErrorFooter from './input-automation-steps-error-footer.vue'
import LoadingUI from './input-automation-steps-loading.vue'
import {
  ackFailedAiOCRRequest,
  retrieveLastOCRRequestStatus,
  saveOCRDocuments,
} from '../api'
import { formatDocumentForSave, normalizePreviousDocuments } from '../utils'
import { SfLog } from '@app/adapters/logger'
import SfModalPersistent from '@app/shared/vue/components/sf-modal/sf-modal-persistent.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import useSfPolling from '@app/shared/vue/composables/use-sf-polling'

import { useOCRDocuments } from '../store/use-ocr-documents'
import { useOCRUI } from '../store/use-ocr-ui'
import humps from 'humps'

interface Props {
  accountId: number
  cancelTooltip?: string
  eager?: boolean
  ledgerId: number
  initialUIState?: UIState
  maxFiles?: number
  reconciliationTextId: number
  shouldCloseOnReview?: boolean
  shouldDeclineProposalOnCancel?: boolean
  shouldDeclineProposalOnEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  cancelTooltip: '',
  initialUIState: UIState.Edit,
  maxFiles: 20,
})

const I18n = useI18n()
const { startPolling } = useSfPolling()
let unsubscribe: null | (() => void)

onBeforeUnmount(() => {
  removeSubscription()
})

const documentStore = useOCRDocuments()
const uiStore = useOCRUI()
uiStore.transitionTo(props.initialUIState)

const { allDocumentIds, documents, maxDocumentNumber } =
  storeToRefs(documentStore)
maxDocumentNumber.value = props.maxFiles

const { uiState, latestOCRRequestId } = storeToRefs(uiStore)

const cancelLabel = I18n.t('ai_ocr_documents.steps.cancel')
const titleLabel = I18n.t('ai_ocr_documents.steps.title')

async function checkForOCRRequestStatus(): Promise<void> {
  uiStore.transitionTo(UIState.Loading)

  try {
    const response = await retrieveLastOCRRequestStatus({
      ledgerId: props.ledgerId,
      reconciliationTextId: props.reconciliationTextId,
      accountId: props.accountId,
    })

    if (!response || !response.aiOcrRequest || !response.aiOcrRequest.status) {
      throw new Error('No response from retrieveLastOCRRequestStatus')
    }

    const {
      localizedErrorMessages = [],
      status = '',
      documents = [],
      id = '',
      reconciliationProposal = { status: '' },
    } = response.aiOcrRequest
    const { channel = '', lastId = 0 } = response.polling

    uiStore.setLatestOCRRequestId(id)
    switch (status) {
      case LastOCRRequestStatus.Retried:
      case LastOCRRequestStatus.Completed: {
        if (localizedErrorMessages.length > 0) {
          documentStore.addDocuments(normalizePreviousDocuments(documents))
          uiStore.transitionTo(UIState.PartialError)
          localizedErrorMessages.forEach((err) =>
            documentStore.addDocumentError(err)
          )
          window.modalCitizen.openModalPortal('input-automation-steps-modal')
          if (
            reconciliationProposal.status ===
            LastOCRRequestProposalStatus.Rejected
          ) {
            uiStore.transitionTo(UIState.Edit)
          }
        } else {
          uiStore.transitionTo(UIState.Edit)
        }

        break
      }
      case LastOCRRequestStatus.Failed:
        documentStore.addDocuments(normalizePreviousDocuments(documents))
        if (localizedErrorMessages.length > 0) {
          localizedErrorMessages.forEach((err) =>
            documentStore.addDocumentError(err)
          )
        }
        uiStore.transitionTo(UIState.Error)
        window.modalCitizen.openModalPortal('input-automation-steps-modal')
        break
      case LastOCRRequestStatus.Sent:
        documentStore.addDocuments(normalizePreviousDocuments(documents))
        uiStore.transitionTo(UIState.Loading)
        window.modalCitizen.openModalPortal('input-automation-steps-modal')
        subscribeToUploadProgress(channel, lastId)
        break
      default:
        uiStore.transitionTo(UIState.Edit)
        break
    }
  } catch (e) {
    uiStore.transitionTo(UIState.Edit)
    SfLog.info('InputAutomationSteps::Error retrieving previous status', {
      extra: {
        companyId: window.Silverfin.currentCompanyId,
        firmId: window.Silverfin.currentFirmId,
        error: e,
      },
    })
  }
}

checkForOCRRequestStatus()

const bodyComponent = computed(() => {
  switch (uiState.value) {
    case UIState.Edit:
      return EditUI
    case UIState.WaitingForMB:
    case UIState.Loading:
      return LoadingUI
    case UIState.Error:
    case UIState.PartialError:
      return ErrorUI
    default:
      return null
  }
})

const footerComponent = computed(() => {
  switch (uiState.value) {
    case UIState.Edit:
      return EditFooter
    case UIState.Error:
    case UIState.PartialError:
      return ErrorFooter
    default:
      return null
  }
})

async function handleClose() {
  if (props.shouldDeclineProposalOnCancel) {
    window.eventHub.$emit('decline-reconciliation-proposal')
  }

  try {
    await ackFailedAiOCRRequest(latestOCRRequestId.value)
  } catch (e) {
    const err = e as Error
    SfLog.info(err.message)
  }
  documentStore.resetStore()
  uiStore.transitionTo(UIState.Edit)
}

async function handleSave(): Promise<void> {
  uiStore.transitionTo(UIState.Loading)

  const payload = allDocumentIds.value.map((id) => {
    const file = documents.value[id]

    if (isProposedDocument(file) || isExistingDocument(file)) {
      return {
        document_attributes: {
          document_id: parseInt(id, 10),
        },
      }
    } else {
      return formatDocumentForSave(id, file)
    }
  })

  try {
    const response = await saveOCRDocuments({
      ledgerId: props.ledgerId,
      reconciliationTextId: props.reconciliationTextId,
      accountId: props.accountId,
      payload,
    })

    if (!response?.channel || (response?.lastId !== 0 && !response?.lastId)) {
      throw new Error(
        I18n.t('ai_ocr_requests.processing_errors.unexpected_error')
      )
    }

    subscribeToUploadProgress(response.channel, response.lastId)
  } catch (e) {
    const err = e as Error
    uiStore.transitionTo(UIState.Error)
    SfLog.info(err.message, {
      extra: {
        companyId: window.Silverfin.currentCompanyId,
        firmId: window.Silverfin.currentFirmId,
        error: e,
      },
    })
  }
}

function subscribeToUploadProgress(endpoint: string, lastId: number): void {
  uiStore.transitionTo(UIState.WaitingForMB)

  removeSubscription()

  unsubscribe = startPolling(endpoint, lastId, () => true, uploadCompleted)
}

function removeSubscription(): void {
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }
}

function uploadCompleted(
  response:
    | string
    | { localized_error_messages: Array<ErrorMessage>; id: string }
): void {
  if (response === 'complete') {
    removeSubscription()
    window.modalCitizen.closeModalPortal()
    documentStore.resetStore()
    uiStore.transitionTo(UIState.Edit)
    window.Turbolinks.visit(window.location.href)
  } else {
    if (typeof response === 'object') {
      removeSubscription()
      uiStore.transitionTo(UIState.Error)

      uiStore.setLatestOCRRequestId(response?.id)
      response?.localized_error_messages?.forEach((err) => {
        const addedDocumentError = documentStore.addDocumentError(
          humps.camelizeKeys(err) as ErrorMessage
        )

        // If we've added at least one document specific error, we should transition to partial error
        if (addedDocumentError) {
          uiStore.transitionTo(UIState.PartialError)
        }
      })

      SfLog.info('Error on input automation upload polling', {
        extra: {
          companyId: window.Silverfin.currentCompanyId,
          firmId: window.Silverfin.currentFirmId,
          error: response,
        },
      })
    }
  }
}
</script>
